/**
 * SchemaMigration (AJIP-16.02.004 · WS4)
 * ---------------------------------------------------------------------
 * Deterministic schema migration.
 *
 * A migration is an object:
 *   { from: integer, to: integer, migrate: (record) => record }
 *
 * runMigrations(record, migrations, targetVersion)
 *   * Rejects records whose schemaVersion > targetVersion.
 *   * If schemaVersion === targetVersion, returns the record unchanged.
 *   * Otherwise applies migrations sequentially, matching (from, to)
 *     tuples in ascending order. Missing step → UnsupportedSchemaVersionError.
 *   * Returns a fresh record whose schemaVersion === targetVersion.
 *   * Migration functions must be pure and deterministic. The runner
 *     validates that each step produces the promised `to` version.
 *
 * Silent destructive migration is prohibited: a migration must set the
 * `to` version explicitly and the runner enforces it.
 */
import {
  UnsupportedSchemaVersionError,
  MigrationError,
} from './PersistenceErrors';

const runMigrations = (record, migrations, targetVersion) => {
  if (!record || typeof record.schemaVersion !== 'number') {
    throw new UnsupportedSchemaVersionError('Record has no schemaVersion');
  }
  if (!Number.isInteger(targetVersion) || targetVersion < 1) {
    throw new UnsupportedSchemaVersionError('targetVersion must be an integer >= 1');
  }
  if (record.schemaVersion > targetVersion) {
    throw new UnsupportedSchemaVersionError('Record schemaVersion is newer than framework supports', {
      recordVersion: record.schemaVersion,
      targetVersion,
    });
  }
  if (record.schemaVersion === targetVersion) return record;

  const path = [];
  let current = record;
  while (current.schemaVersion < targetVersion) {
    const step = (migrations || []).find(
      (m) => m && m.from === current.schemaVersion && m.to === current.schemaVersion + 1,
    );
    if (!step) {
      throw new UnsupportedSchemaVersionError(
        `No migration from schema v${current.schemaVersion} to v${current.schemaVersion + 1}`,
      );
    }
    if (typeof step.migrate !== 'function') {
      throw new MigrationError(`Migration ${step.from}→${step.to} has no migrate() function`);
    }
    let next;
    try {
      next = step.migrate(current);
    } catch (e) {
      throw new MigrationError(`Migration ${step.from}→${step.to} threw: ${e.message}`, { cause: e });
    }
    if (!next || next.schemaVersion !== step.to) {
      throw new MigrationError(
        `Migration ${step.from}→${step.to} did not produce schemaVersion ${step.to}`,
      );
    }
    path.push({ from: step.from, to: step.to });
    current = next;
  }
  return { record: current, path: Object.freeze(path) };
};

export { runMigrations };
